import "react-color-palette";

import { CssBaseline } from "@mui/material";
import Layout from "./components/layout/Layout";
import { Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { NotFound } from "./pages/NotFound";
import { ServicesPage } from "./pages/ServicesPage";
import ServicePage from "./pages/ServicePage";
import { AlertEventsPage } from "./pages/AlertEventsPage";
import OAuthSetupResultPage from "./pages/oauth/OAuthSetupResultPage";
import ConfigurationLayoutPage from "./pages/config/ConfigurationLayoutPage";
import ToolIntegrationsConfigPage from "./pages/config/ToolIntegrationsConfigPage";
import AlertsConfigPage from "./pages/config/AlertsConfigPage";
import AlertSchemasConfigPage from "./pages/config/AlertSchemasConfigPage";
import ServiceTemplatesConfigPage from "./pages/config/ServiceTemplatesConfigPage";
import KpisConfigPage from "./pages/config/KpisConfigPage";
import UsersConfigPage from "./pages/config/UsersConfigPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import LoginPage from "./pages/LoginPage";
import DashboardsPage from "./pages/DashboardsPage";
import { loginService } from "./services/loginService";
import GoogleOAuthCallbackPage from "./pages/oauth/GoogleOAuthCallbackPage";

const RequireAuth: React.FC<{ children: ReactNode }> = ({ children }) => {
  if (!loginService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const RedirectToDefaultDashboardServices: React.FC = () => {
  return <Navigate to="/dashboards" replace />;
};

const App: React.FC = () => {
  return (
    <>
      <meta name="viewport" content="initial-scale=1, width=device-width" />
      <CssBaseline enableColorScheme />
      <Routes>
        <Route
          path="/login"
          element={
            loginService.isAuthenticated() ? (
              <Navigate to="/dashboards" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="/auth/verify-fallback"
          element={
            loginService.isAuthenticated() ? (
              <Navigate to="/dashboards" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="/auth/recover-fallback"
          element={
            loginService.isAuthenticated() ? (
              <Navigate to="/dashboards" replace />
            ) : (
              <LoginPage />
            )
          }
        />
        <Route path="/oauth/google/callback" element={<GoogleOAuthCallbackPage />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="dashboards" replace />} />
          <Route path="integration-setup/:integrationId/success" element={<OAuthSetupResultPage result="success" />} />
          <Route path="integration-setup/:integrationId/failed" element={<OAuthSetupResultPage result="failed" />} />
          <Route path="dashboards" element={<DashboardsPage />} />
          <Route
            path="dashboards/:dashboardId/services"
            element={<ServicesPage />}
          />
          <Route
            path="dashboards/:dashboardId/services/:serviceId"
            element={<ServicePage />}
          />

          <Route path="services" element={<RedirectToDefaultDashboardServices />} />
          <Route
            path="services/:serviceId"
            element={<RedirectToDefaultDashboardServices />}
          />

          <Route path="event-analytics" element={<ComingSoonPage />} />
          <Route path="war-room" element={<ComingSoonPage />} />
          <Route path="coming-soon" element={<Navigate to="/event-analytics" replace />} />
          <Route path="config" element={<ConfigurationLayoutPage />}>
            <Route index element={<Navigate to="tool-integrations" replace />} />
            <Route
              path="tool-integrations"
              element={<ToolIntegrationsConfigPage />}
            />
            <Route path="alerts" element={<AlertsConfigPage />} />
            <Route path="alert-schemas" element={<AlertSchemasConfigPage />} />
            <Route
              path="service-templates"
              element={<ServiceTemplatesConfigPage />}
            />
            <Route path="kpis" element={<KpisConfigPage />} />
            <Route path="users" element={<UsersConfigPage />} />
          </Route>
          <Route
            path="integrations"
            element={<Navigate to="/config/tool-integrations" replace />}
          />
          <Route path="alerts" element={<Navigate to="/config/alerts" replace />} />
          <Route
            path="service-templates"
            element={<Navigate to="/config/service-templates" replace />}
          />
          <Route path="kpis" element={<Navigate to="/config/kpis" replace />} />
          <Route
            path="alert-providers"
            element={<Navigate to="/config/alert-schemas" replace />}
          />
          <Route path="alerts/:alertId" element={<AlertEventsPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
};

export default App;
